/**
 * Os campos personalizados da organização (#568, ADR-0023): o catálogo que
 * gestor e admin mantêm em Configurações.
 *
 * Cada campo é de um cadastro (`entidade`). O pedido de exame e o tratamento
 * só existem nas organizações com a funcionalidade "clinica" ligada; sem ela,
 * as rotas respondem 404 para esses dois.
 *
 * O registro guarda o valor pelo `id` do campo e, nas listas, pelo `id` da
 * opção: renomear não mexe em nada que já foi preenchido. Campo e opção não se
 * apagam, arquivam.
 */

import { z } from 'zod';

export const ENTIDADES_DE_CAMPO = [
  'cliente',
  'oportunidade',
  'pedido_de_exame',
  'tratamento',
] as const;
export const EntidadeDeCampoSchema = z.enum(ENTIDADES_DE_CAMPO);
export type EntidadeDeCampo = z.infer<typeof EntidadeDeCampoSchema>;

export const TIPOS_DE_CAMPO = [
  'texto_curto',
  'texto_longo',
  'numero',
  'dinheiro',
  'data',
  'sim_nao',
  'lista_unica',
  'lista_multipla',
] as const;
export const TipoDeCampoSchema = z.enum(TIPOS_DE_CAMPO);
export type TipoDeCampo = z.infer<typeof TipoDeCampoSchema>;

export const OpcaoDeCampoSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  /** Some das escolhas novas; quem já a escolheu continua com ela. */
  arquivada: z.boolean(),
});
export type OpcaoDeCampo = z.infer<typeof OpcaoDeCampoSchema>;

export const CampoPersonalizadoSchema = z.object({
  id: z.string().uuid(),
  entidade: EntidadeDeCampoSchema,
  nome: z.string(),
  tipo: TipoDeCampoSchema,
  /** Só nas listas; vazio nos outros tipos. Na ordem em que aparecem. */
  opcoes: z.array(OpcaoDeCampoSchema),
  obrigatorio: z.boolean(),
  /**
   * Só na oportunidade: o valor da etapa a partir da qual o campo é
   * obrigatório. `null` com `obrigatorio` é obrigatório desde o começo.
   */
  obrigatorio_desde_etapa: z.string().nullable(),
  ordem: z.number().int().min(0),
  arquivado: z.boolean(),
  /** Vai de volta no PATCH: quem salvou antes faz o segundo receber 409. */
  versao: z.number().int().min(1),
});
export type CampoPersonalizado = z.infer<typeof CampoPersonalizadoSchema>;

export const CamposPersonalizadosSchema = z.object({
  /** Do cadastro pedido, arquivados também, na ordem do catálogo. */
  itens: z.array(CampoPersonalizadoSchema),
});
export type CamposPersonalizados = z.infer<typeof CamposPersonalizadosSchema>;

const NomeDoCampo = z.string().max(60);
const NomeDaOpcao = z.string().max(80);

export const CriarCampoPersonalizadoSchema = z
  .object({
    entidade: EntidadeDeCampoSchema,
    nome: NomeDoCampo,
    tipo: TipoDeCampoSchema,
    /** Obrigatório nas listas, com pelo menos uma; proibido nos outros tipos. */
    opcoes: z
      .array(z.object({ nome: NomeDaOpcao }).strict())
      .max(500)
      .optional(),
    obrigatorio: z.boolean().optional(),
    obrigatorio_desde_etapa: z.string().max(200).nullable().optional(),
  })
  .strict();
export type CriarCampoPersonalizado = z.infer<typeof CriarCampoPersonalizadoSchema>;

/**
 * O que muda. Ausente fica como está. O tipo não muda: um campo de texto que
 * vira lista deixaria os valores sem opção para ler (ADR-0023).
 */
export const EditarCampoPersonalizadoSchema = z
  .object({
    versao: z.number().int().min(1),
    nome: NomeDoCampo.optional(),
    /**
     * A lista inteira, na ordem nova. Toda opção que existia volta (com o `id`
     * dela, arquivada se for o caso); a que vem sem `id` é nova.
     */
    opcoes: z
      .array(
        z
          .object({
            id: z.string().uuid().nullable().optional(),
            nome: NomeDaOpcao,
            arquivada: z.boolean().optional(),
          })
          .strict(),
      )
      .max(500)
      .optional(),
    obrigatorio: z.boolean().optional(),
    obrigatorio_desde_etapa: z.string().max(200).nullable().optional(),
    arquivado: z.boolean().optional(),
  })
  .strict();
export type EditarCampoPersonalizado = z.infer<typeof EditarCampoPersonalizadoSchema>;

export const OrdenarCamposPersonalizadosSchema = z
  .object({
    entidade: EntidadeDeCampoSchema,
    /** Todos os campos do cadastro, arquivados também, uma vez cada. */
    ids: z.array(z.string().uuid()).max(1000),
  })
  .strict();
export type OrdenarCamposPersonalizados = z.infer<typeof OrdenarCamposPersonalizadosSchema>;

// ── Os valores, no registro ─────────────────────────────────────────────

/**
 * O valor de um campo, na forma do tipo dele: texto (texto curto, texto longo,
 * data `AAAA-MM-DD` e lista de uma escolha, pelo id da opção), número,
 * sim/não, valor em R$ e lista de várias escolhas (os ids das opções).
 */
export const ValorDeCampoSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.object({ quantia_micros: z.number().int(), moeda: z.literal('BRL') }),
  z.array(z.string()),
]);
export type ValorDeCampo = z.infer<typeof ValorDeCampoSchema>;

/** Os valores de um registro, pelo id do campo. Campo vazio não aparece. */
export const ValoresDeCamposSchema = z.record(z.string(), ValorDeCampoSchema);
export type ValoresDeCampos = z.infer<typeof ValoresDeCamposSchema>;

/**
 * O que muda nos valores de um registro: só os campos que mudaram, pelo id.
 * `null` esvazia o campo. O servidor confere cada valor contra o catálogo;
 * campo que não é deste cadastro (ou arquivado) é 404.
 */
export const MudancaDeCamposSchema = z.record(z.string().max(64), ValorDeCampoSchema.nullable());
export type MudancaDeCampos = z.infer<typeof MudancaDeCamposSchema>;
